<template>
  <ListViewBanner :add-route="{ name: 'app_framework_new' }">
    Frameworks
  </ListViewBanner>

  <DefaultSearch @search="searchParams = $event" />

  <AppContainer max-width="max-w-7xl">
    <AppTable>
      <AppTableHeader>
        <th>Name</th>
        <th class="w-[150px]">
          Short Name
        </th>

        <th class="text-right w-[100px]">
          Sort Order
        </th>

        <th class="text-right w-[180px]">
          No. of goals
        </th>

        <th class="w-[150px]">
          <span class="sr-only">Add Goal</span>
        </th>

        <th class="w-[150px]">
          <span class="sr-only">Actions</span>
        </th>
      </AppTableHeader>

      <AppTableBody
        :loading="loading"
        :no-results="!frameworks.length"
        :colspan="5"
      >
        <template v-if="frameworks.length">
          <tr
            v-for="framework in frameworks"
            :key="framework.id"
            class="overflow-visible"
          >
            <td>
              <RouterLink
                class="border-secondary-subtle"
                :to="{ name: `app_framework_show`, params: { framework: framework.id } }"
              >
                {{ framework.name }}
              </RouterLink>

              <template v-if="framework.archived">
                <BadgeArchived />
              </template>
            </td>

            <td>
              {{ framework.shortName }}
            </td>

            <td class="text-right">
              {{ framework.sortOrder }}
            </td>

            <td class="text-right">
              {{ framework.goalCount }}
            </td>

            <td
              class="flex justify-end !mr-0"
              :class="{ 'py-4' : framework.archived }"
            >
              <RouterLink
                class="rounded !border btn-outline no-underline hover:bg-gray-200 text-sm text-gray-600 !font-normal !px-1 ml-3"
                :to="{ name: `app_framework_show`, params: { framework: framework.id } }"
              >
                <FontAwesomeIcon
                  :icon="faPlus"
                  class="text-xs"
                />
                Add goal
              </RouterLink>
            </td>

            <td>
              <template v-if="framework.archived">
                <RestoreButton @click="entityToRestore = framework" />
              </template>

              <template v-else>
                <AppDropdown
                  class="mx-auto"
                  type="actions"
                >
                  <AppDropdownItem
                    text="Edit"
                    :to="{ name: 'app_framework_edit', params: { framework: framework.id }}"
                  />

                  <AppDropdownItem
                    text="Archive"
                    @click="entityToArchive = framework"
                  />
                </AppDropdown>
              </template>
            </td>
          </tr>
        </template>
      </AppTableBody>
    </AppTable>

    <AppPagination
      v-model:itemsPerPage="paginationData.itemsPerPage"
      v-model:currentPage="paginationData.page"
      :items-per-page="paginationData?.itemsPerPage"
      :current-page="paginationData?.page"
      :total-items="paginationData?.total"
      @page-change="searchParams.page = $event"
      @items-per-page-change="searchParams.itemsPerPage = $event"
    />
  </AppContainer>

  <!-- Archive Modal -->
  <template v-if="entityToArchive">
    <ConfirmationModal
      title="Archive Framework"
      intent="warning"
      :processing="archiving"
      @confirm="archiveEntity"
      @close="entityToArchive = false"
    >
      <span class="block">Name: {{ entityToArchive?.name }}</span>
      Are you sure you want to <strong>archive</strong> this Framework?
    </ConfirmationModal>
  </template>

  <!-- Restore Modal -->
  <template v-if="entityToRestore">
    <ConfirmationModal
      title="Restore Framework"
      intent="confirmation"
      :processing="restoring"
      @confirm="restoreEntity"
      @close="entityToRestore = false"
    >
      <p>
        <span class="block">Name: {{ entityToRestore?.name }}</span>
        Are you sure you want to <strong>restore</strong> this Framework?
      </p>
    </ConfirmationModal>
  </template>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { ref, watchEffect } from 'vue';

import { AppContainer, AppPagination } from '@/components/app';
import { BadgeArchived } from '@/components/badges';
import { ListViewBanner } from '@/components/banners';
import { RestoreButton } from '@/components/buttons';
import { AppDropdown, AppDropdownItem } from '@/components/dropdowns';
import { DefaultSearch } from '@/components/forms/search';
import { ConfirmationModal } from '@/components/modals';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';

import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { useEntityArchiver, useEntityRestorer, useHttp } from '@/composables';

const loading = ref(true);
const frameworks = ref([]);
const searchParams = ref({ page: 1 });
const paginationData = ref({ page: 1, itemsPerPage: 10 });

const loadFrameworks = async () => {
  loading.value = true;
  frameworks.value = [];

  const response = await useHttp().get('/frameworks', searchParams.value);

  if (response.statusCode === HttpStatusCode.Ok) {
    frameworks.value = response.data.items;
    paginationData.value = response.data.pagination;
  }

  loading.value = false;
};

const { archiving, entityToArchive, archiveEntity } = useEntityArchiver('frameworks', loadFrameworks);
const { restoring, entityToRestore, restoreEntity } = useEntityRestorer('frameworks', loadFrameworks);

watchEffect(loadFrameworks);
</script>
